import * as THREE from 'three';
import { WebglScene } from '../scene';
import { Cleaner } from '../helpers/cleaner';
import { Material } from '../helpers/material';
import { WebglHullEntity } from '../hullEntity';
import { OBJLoader } from '../obj/OBJLoader';
import { BINLoader } from '../obj/BINLoader';
import { Coating, Component, ComponentConstants, Entity, Floor, FloorCoatingArea, math, Scene, SceneConstants, Wall } from '@rhinov/savane-js';

export class FloorGeneratorHull {

    public group: THREE.Group;
    public hull: THREE.Group;

    private savane: Scene;
    private scene: WebglScene;
    private _entity: Entity | null = null;

    constructor(obj: string, savane: Scene, scene: WebglScene) {
        this.savane = savane;
        this.scene = scene;
        let loader;
        if (!scene.settings.hullbin) {
            loader = new OBJLoader();
            this.hull = loader.parse(obj);
        }
        else {
            loader = new BINLoader();
            this.hull = loader.parse(obj).object;
        }
        this.hull.name = "FloorGeneratorHull";
        this.group = new THREE.Group();
        this.group.add(this.hull);
        //updated entity
        this._entity = null;
        this._process();
        this.hideCeiling();
    }

    static create(obj: string, savane: Scene, scene: WebglScene) {
        return new FloorGeneratorHull(obj, savane, scene);
    }

    dispose() : void {
        this.hull.traverse((child: any) => {
            if (child.geometry) {
                child.geometry.dispose();
            }
            if (child.body) {
                this.scene.physics.destroy(child.body);
            }
            if (child.material) {
                Cleaner.cleanMaterial(child.material);
            }
            if (child.cubeCamera) {
                child.cubeCamera.renderTarget.dispose();
            }
        });
    }

    showCeiling() : void {
        let traversing = function(child: THREE.Object3D) {
            if (child.name.startsWith("axo")) {
                child.castShadow = true;
                child.receiveShadow = true;
                child.layers.set(0);
            }
        };
        this.hull.traverse(traversing.bind(this));
    }

    hideCeiling() : void {
        let traversing = function(child: THREE.Object3D) {
            // Ceiling always hidden
            let hide = false;
            if (child.name.startsWith("axo")) {
                hide = true;
            }

            if (hide === true) {
                child.castShadow = false;
                child.receiveShadow = false;
                child.layers.set(1);
            }
        };
        this.hull.traverse(traversing.bind(this));
    }

    showUpperFloor(floor: Floor) : void {
        if (!floor) return;

        this.hull.traverse((child: THREE.Object3D) => {
            let entity = this.getEntityFromChild(child);
            if (entity && entity.floor && entity.floor.height > floor.height) {
                child.layers.set(0);
            }
        });
    }

    hideUpperFloor(floor: Floor) : void {
        this.hull.traverse((child: THREE.Object3D) => {
            let entity = this.getEntityFromChild(child);
            if (entity && entity.floor && entity.floor.height > floor.height) {
                child.layers.set(1);
            }
        });
    }

    update(entity: Entity) : void {
        this._entity = entity;
        this.hull.traverse(this._processChild.bind(this));
        this._entity = null;
    }

    updateCoatingParameters(entity: Entity) {
        let traversing = (child: any) => {
            if (child.name.startsWith("Floor") || child.name.startsWith("axo_Floor")) {
                //add node to wall
                let floorParse = child.name.split('_');
                
                let parentId = Number(floorParse[1]);
                if (child.name.startsWith("axo_Floor")) {
                    parentId = Number(floorParse[2]);
                }

                if (parentId === entity.id) {
                    let parent = this.savane.getDeepChild(parentId);

                    if (parent !== null) {
                        if (parent.isRoomEntity()) {
                            let coatings: any[] = parent.getComponents(ComponentConstants.ComponentType.Coating);
                            let hangType = Coating.HangType.floor;
                            if (child.name.startsWith("axo_FloorCeiling")) {
                                hangType = Coating.HangType.ceiling;
                            }

                            if (coatings !== null) {
                                for (let i = 0; i < coatings.length; i++) {
                                    if (coatings[i].hangType === hangType) {
                                        let material = child.material as THREE.MeshPhongMaterial;
                                        if (material.map) {
                                            material.map.rotation = (coatings[i].rotation * Math.PI) / 180;
                                            material.map.offset.x = (coatings[i].offset[0]);
                                            material.map.offset.y = (coatings[i].offset[1]);
                                            material.map.repeat.x = (material.map as any).originalRepeat.x * (coatings[i].repeat[0]);
                                            material.map.repeat.y = (material.map as any).originalRepeat.y * (coatings[i].repeat[1]);
                                        }
                                    }
                                }
                            }
                        } else if (parent.isFloorEntity()) {
                            let coatingAreaNb = -1;
                            if (child.name.startsWith("axo_Floor")) {
                                coatingAreaNb = Number(floorParse[3]) - 1;
                            } else {
                                coatingAreaNb = Number(floorParse[2]) - 1;
                            }
                            let coatings: any[] = parent.getComponents(ComponentConstants.ComponentType.FloorCoatingArea);
                            if (coatings !== null) {
                                let material = child.material as THREE.MeshPhongMaterial;
                                if (material.map) {
                                    material.map.rotation = (coatings[coatingAreaNb].rotation * Math.PI) / 180;
                                    material.map.offset.x = (coatings[coatingAreaNb].offset[0]);
                                    material.map.offset.y = (coatings[coatingAreaNb].offset[1]);
                                    material.map.repeat.x = (material.map as any).originalRepeat.x * (coatings[coatingAreaNb].repeat[0]);
                                    material.map.repeat.y = (material.map as any).originalRepeat.y * (coatings[coatingAreaNb].repeat[1]);
                                }
                            }

                        }
                    }
                }
            }
        };

        this.hull.traverse(traversing);
    }

    UpdateComponent(component: Component, offset: math.vec2) {
        let childs = this.getChildsFromId(component.entity!.id);
        let local = new THREE.Matrix4();
        if (component.entity!.isWallEntity()) {
            let wall = component.entity as Wall;
            let xAxis = new THREE.Vector3(wall.wallDirection[0], wall.wallDirection[2], wall.wallDirection[1]);
            let zAxis = new THREE.Vector3(wall.normal[0], wall.normal[2], wall.normal[1]);
            local.makeBasis(new THREE.Vector3(0, 1, 0), xAxis, zAxis);
        }
        childs.forEach(element => {
            element.position.add(new THREE.Vector3(offset[0] / 100, offset[1] / 100, 0).applyMatrix4(local));
            element.updateMatrixWorld(true);
        });
        this.scene.render();
    }

    filterFloor(floor: Floor) : void {
        this.hull.traverse((child: THREE.Object3D) => {
            let entity = this.getEntityFromChild(child);
            if (floor !== null) {
                if (child.name.startsWith("InterFloor")) {
                    child.layers.set(1);
                }
                if (entity !== null && (floor.getDeepChild(entity.id) || entity === floor)) {
                    child.layers.set(0);
                } else {
                    child.layers.set(1);
                }
            } else {
                child.layers.set(0);
            }

        });
    }

    static getIdFromChild(child: THREE.Object3D) : number | null {
        let result: number | null = null;

        let parse = child.name.split('_');
        if (child.name.startsWith("FloorPlinth")) {
            result = Number(parse[2]);
        } else if (child.name.startsWith("Floor")) {
            result = Number(parse[1]);
        } else if (child.name.startsWith("axo_Floor")) {
            result = Number(parse[2]);
        } else if (child.name.startsWith("InterFloor")) {
            result = Number(parse[1]);
        } else if (child.name.startsWith("axo_InterFloor")) {
            result = Number(parse[2]);
        } else if (child.name.startsWith("Step")) {
            result = Number(parse[1]);
        } else if (child.name.startsWith("axo_Step")) {
            result = Number(parse[2]);
        } else if (child.name.startsWith("CounterStep")) {
            result = Number(parse[1]);
        } else if (child.name.startsWith("axo_CounterStep")) {
            result = Number(parse[2]);
        } else if (child.name.startsWith("BackStep")) {
            result = Number(parse[1]);
        } else if (child.name.startsWith("axo_BackStep")) {
            result = Number(parse[2]);
        } else if (child.name.startsWith("Limon")) {
            result = Number(parse[1]);
        } else if (child.name.startsWith("axo_Limon")) {
            result = Number(parse[2]);
        }

        return result ? Math.abs(result) : null;
    }

    getChildsFromId(id: number) : Array<THREE.Object3D> {
        let result: Array<THREE.Object3D> = [];
        this.hull.traverse((child: THREE.Object3D) => {
            if (id === FloorGeneratorHull.getIdFromChild(child)) {
                result.push(child);
            }
        });
        return result;
    }

    getEntityFromChild(child: THREE.Object3D) : Entity |null {
        let id = FloorGeneratorHull.getIdFromChild(child);
        if (id) {
            return this.savane.getDeepChild(id);
        }
        return null;
    }

    _process() : void {
        this.hull.traverse(this._processChild.bind(this));
    }

    _handleInterFloor(child: THREE.Mesh) : void {
        if (!child.name.startsWith("InterFloor")) {
            return;
        }

        // Default white color assignment to interfloors
        child.material = Material.default(new THREE.Color(0x505050).convertSRGBToLinear(), null, {
            
        });
    }

    _handleFloor(child: THREE.Mesh, entity: Entity) : void {
        if (!(child.name.startsWith("Floor") || child.name.startsWith("axo_Floor") ||
              child.name.startsWith("Step") || child.name.startsWith("axo_Step") ||
              child.name.startsWith("CounterStep") || child.name.startsWith("axo_CounterStep") ||
              child.name.startsWith("BackStep") || child.name.startsWith("axo_BackStep") ||
              child.name.startsWith("Limon") || child.name.startsWith("axo_Limon"))) {
            return;
        }

        //add node to wall
        let floorParse = child.name.split('_');
        let parentId = Number(floorParse[1]);
        if (child.name.startsWith("axo_Floor")) {
            parentId = Number(floorParse[2]);
        }
        let slope = child.name.startsWith("axo_FloorSlope");
        let parent = this.savane.getDeepChild(parentId);

        if (!parent) {
            return;
        }

        if (entity && entity.id !== parentId) {
            return;
        }

        let { coatings, config, hangType, coatingAreaNb } = this.getCoatingParameters(child, parent);
        let found = false;

        if (coatings !== null) {
            // Assign material to mesh
            if (parent.isRoomEntity()) {
                let plinthOverride = false;

                if (child.name.startsWith("FloorPlinth")) {
                    let wallId = Number(floorParse[2]);
                    let wall = this.savane.getDeepChild(wallId)!;
                    let wallCoatings = wall.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;
                    for (let i = 0; i < wallCoatings.length; ++i) {
                        if (wallCoatings[i].hangType == hangType) {
                            coatings = wallCoatings;
                            plinthOverride = true;
                        }
                    }
                }

                if (!plinthOverride) {
                    hangType = Coating.HangType.floor;
                    if (child.name.startsWith("axo_FloorCeiling") || child.name.startsWith("FloorPoolEdge")) {
                        hangType = Coating.HangType.ceiling;
                    }
                }

                for (let i = 0; i < coatings.length; i++) {
                    if (coatings[i].hangType === hangType) {
                        Material.setMaterialFromCoating(child, coatings[i], true, config ?? undefined, this.scene, false, null);
                        (child as any).coatingId = coatings[i].coatingId;
                        if (coatings[i].floorGeneratorSettings && coatings[i].floorGeneratorSettings.jointCoating && !config) {
                            child.material = Material.default(new THREE.Color(coatings[i].floorGeneratorSettings.jointCoating.colors[0].realColor).convertSRGBToLinear(), null, {
                                
                            });
                        }
                        found = true;
                        break;
                    }
                }
            }
            else if (parent.isWallEntity()) {
                if ((coatings !== null) && (coatings.length > 0)) {
                    for (let i = 0; i < coatings.length; i++) {
                        if (slope) {
                            if (coatings[i].hangType === hangType) {
                                Material.setMaterialFromCoating(child, coatings[i], true, config ?? undefined, this.scene, false, null);
                                (child as any).coatingId = coatings[i].coatingId;
                                found = true;
                                break;
                            }
                        } else {
                            if (coatings[i].hangType === hangType) {
                                Material.setMaterialFromCoating(child, coatings[i], true, config ?? undefined, this.scene, false, null);
                                (child as any).coatingId = coatings[i].coatingId;
                                found = true;
                                break;
                            }
                        }
                    }
                }
            }
            else if (parent.isFloorEntity()) {
                Material.setMaterialFromCoating(child, coatings[coatingAreaNb], true, config ?? undefined, this.scene, false, null);
                (child as any).coatingId = coatings[coatingAreaNb].coatingId;
                if (coatings[coatingAreaNb].floorGeneratorSettings && coatings[coatingAreaNb].floorGeneratorSettings.jointCoating && !config) {
                    child.material = Material.default(new THREE.Color(coatings[coatingAreaNb].floorGeneratorSettings.jointCoating.colors[0].realColor).convertSRGBToLinear(), null, {
                        
                    });
                }
                found = true;
            }
            else if (parent.isGeometryPrimitiveEntity() || parent.isGeometryGroupEntity()) {
                for (let i = 0; i < coatings.length; i++) {
                    if (coatings[i].hangType === Coating.HangType.technicalElement) {
                        Material.setMaterialFromCoating(child, coatings[i], true, config ?? undefined, this.scene, false, null);
                        (child as any).coatingId = coatings[i].coatingId;
                        found = true;
                        break;
                    }
                }
            }
            else if (parent.isStaircaseEntity()) {
                for (let i = 0; i < coatings.length; i++) {
                    Material.setMaterialFromCoating(child, coatings[i], true, config ?? undefined, this.scene, false, null);
                    (child as any).coatingId = coatings[i].coatingId;
                    found = true;
                    break;
                }
            }
        }

        // Coating not found, assign default material
        if (!found) {
            // Default color assignment when no coating
            child.material = Material.default(new THREE.Color(0xffffff), null, {
                
            });
        }

        if (!entity && (parent.isGeometryPrimitiveEntity() || parent.isWallEntity() || (parent.isRoomEntity() && child.name.startsWith("axo_FloorCeiling")))) {
            if (child.isMesh) {
                let self = this;
                child.onBeforeRender = function(renderer, scene, camera, geometry, material, group) {
                    if (self.scene.hullTransparency == false) {
                        material.transparent = false;
                        material.needsUpdate = true;
                        return;
                    }
                    if (camera.parent && camera.parent.type === "CubeCamera") return;

                    let direction = new THREE.Vector3();
                    camera.getWorldDirection(direction);
                    let target = (self.scene.defaultCamera as any).target;
                    for (let i = 0; i < self.scene.planCameras.length; ++i) {
                        if (camera === self.scene.planCameras[i].object) {
                            target = (self.scene.planCameras[i] as any).target;
                        }
                    }
                    if (!target) return;
                    let distanceToTarget = new THREE.Vector3().subVectors(target, camera.position).length();
                    let center = new THREE.Vector3();
                    if (!geometry.boundingBox) {
                        geometry.computeBoundingBox();
                    }
                    geometry.boundingBox!.getCenter(center);
                    let distanceToCenter = new THREE.Vector3().subVectors(center, camera.position).length();
                    if (distanceToTarget > distanceToCenter) {
                        material.transparent = true;
                        material.opacity = 0.05;
                    } else {
                        material.transparent = false;
                    }
                    material.needsUpdate = true;
                };
            }
        }
    }

    getCoatingParameters(child: THREE.Object3D, parent: Entity) : { coatings: Array<Coating>, config: number | null, hangType: Coating.HangType | null, coatingAreaNb: number } {
        let floorParse = child.name.split('_');

        if (!parent) {
            let parentId = Number(floorParse[1]);
            if (child.name.startsWith("axo_Floor")) {
                parentId = Number(floorParse[2]);
            }
            parent = this.savane.getDeepChild(parentId)!;
        }

        let coatingAreaNb: number = -1;
        let hangType: Coating.HangType | null = null;
        let coatings: Array<Coating> = [];
        let config: number | null = null;

        if (!parent) {
            return { coatings, config, hangType, coatingAreaNb };
        }

        if (parent.isRoomEntity()) {
            coatings = parent.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;
            if (floorParse.length === 5) {
                config = Number(floorParse[4]);
            }
            if (child.name.startsWith("FloorPlinth")) {
                config = Number(floorParse[9]);
                hangType = (floorParse[3] === 'Direct' ? Coating.HangType.plinthDirect : Coating.HangType.plinthUndirect)
            }
            if (child.name.startsWith("axo_FloorCeiling")) {
                config = Number(floorParse[6]);
            }
            if (child.name.startsWith("FloorPoolEdge")) {
                config = Number(floorParse[5]);
            }
            if (child.name.startsWith("FloorGeometryPrimitive")) {
                config = Number(floorParse[3]);
                hangType = Coating.HangType.floor;
            }
        } else if (parent.isWallEntity()) {
            coatings = parent.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;
            if (child.name.startsWith("axo_Floor")) { // slope
                hangType = (floorParse[5] === 'Direct' ? Coating.HangType.slopeDirect : Coating.HangType.slopeUndirect);
                config = Number(floorParse[7]);
            } else {
                hangType = (floorParse[4] === 'Direct' ? Coating.HangType.wallDirect:  Coating.HangType.wallUndirect);
                if (floorParse[4] === 'Top') hangType = Coating.HangType.wallTop;
                if (floorParse[4] === 'Bottom') hangType = Coating.HangType.wallBottom;
                if (floorParse[4] === 'Left') hangType = Coating.HangType.wallLeftSide;
                if (floorParse[4] === 'Right') hangType = Coating.HangType.wallRightSide;
                config = Number(floorParse[6]);
            }
        } else if (parent.isFloorEntity()) {
            if (child.name.startsWith("axo_Floor")) {
                coatingAreaNb = Number(floorParse[3]) - 1;
                if (floorParse.length === 7) {
                    config = Number(floorParse[6]);
                }
                if (floorParse[4] === '0') config = null;
            } else {
                coatingAreaNb = Number(floorParse[2]) - 1;
                if (floorParse.length === 6) {
                    config = Number(floorParse[5]);
                    if (floorParse[3] === '0') config = null;
                }
            }
            coatings = parent.getComponents(ComponentConstants.ComponentType.FloorCoatingArea) as Array<FloorCoatingArea>;
        } else if (parent.isGeometryPrimitiveEntity() || parent.isGeometryGroupEntity()) {
            coatings = parent.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;
            if (child.name.startsWith("axo_Floor")) {
                config = Number(floorParse[6]);
            } else {
                config = Number(floorParse[5]);
            }
        } else if (parent.isStaircaseEntity()) {
            let searchedUsemtlName;
            coatings = parent.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;

            if (child.name.indexOf("CounterStep") !== -1) {
                searchedUsemtlName = '2';
            }
            else {
                if (child.name.indexOf("BackStep") !== -1) {
                    searchedUsemtlName = '5';
                }
                else {
                    if (child.name.indexOf("Step") !== -1) {
                        searchedUsemtlName = '1';
                    }
                    else {
                        if (child.name.indexOf("Limon") !== -1) {
                            searchedUsemtlName = '3';
                        }                        
                    }
                }
            }

            for (let i = coatings.length - 1 ; i >= 0 ; i--) {
                if (coatings[i].usemtlName !== searchedUsemtlName) {
                  coatings.splice(i, 1);
                }
            }

            if (child.name.startsWith("axo_Floor")) {
                config = Number(floorParse[6]);
            } else {
                config = Number(floorParse[5]);
            }
        }
        return { coatings, config, hangType, coatingAreaNb };
    }

    _processChild(child: any) : void {
        if (!this._entity) {
            if (child.isMesh) {
                let euler = new THREE.Euler(Math.PI * 0.5, 0, 0, 'XYZ');
                child.geometry.applyMatrix4(new THREE.Matrix4().makeRotationFromEuler(euler));
                child.geometry.applyMatrix4(new THREE.Matrix4().makeScale(0.01, 0.01, 0.01));
            }

            //hull creation - link elements to scene entities
            let entity = this.getEntityFromChild(child);
            if (entity) {
                if (this.scene.interactionWithItemAllowed(entity)) {
                    this.scene.planEntities.push(new WebglHullEntity(entity, this.scene, child, this, false, false));
                } else {
                    this.scene.nonInteractivePlanEntities.push(new WebglHullEntity(entity, this.scene, child, this, false, true));
                }
            }
        }

        //Enable shadow
        child.receiveShadow = true;
        child.castShadow = true;

        this._handleFloor(child, this._entity!);
        this._handleInterFloor(child);

        if (!child.material && child.isMesh) {
            child.material = Material.default(new THREE.Color(0xffffff), null, {
                
            });
        }

        // apply customCoating
        for (let i = 0; i < this.savane.children.length; ++i) {
            let floor = this.savane.children[i] as Floor;
            if (floor.entityType === SceneConstants.EntityType.Floor) {
                let name = child.name.replace(/_[a-fA-F0-9]{24}_\d+/, '');
                let custom = floor.getCustomCoating(name);
                if (custom) {
                    Material.setMaterialFromCoating(child, custom.coating, true, undefined, this.scene, false, null);
                    (child as any).coatingId = custom.coating.coatingId;
                }
            }
        }
    }

}
